/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
/* eslint-disable eslint-comments/no-restricted-disable */

import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import type { HorizontalNavPlansEntrypointProps } from './ui/HorizontalNavPlans.entrypoint.types.tsx';
import parameters from './ui/__generated__/HorizontalNavPlansQuery$parameters';
import type { HorizontalNavPlansQuery } from './ui/__generated__/HorizontalNavPlansQuery.graphql';

const NAVIGATION_ITEMS_PAGE_SIZE = 75;

function toPlanScenarioAri({
	siteId,
	activationId,
	planId,
	scenarioId,
}: {
	siteId: string;
	activationId: string;
	planId: string;
	scenarioId: string;
}) {
	return `ari:cloud:jira:${siteId}:plan-scenario/activation/${activationId}/${planId}/scenario/${scenarioId}`;
}

function toPlanAri({
	siteId,
	activationId,
	planId,
}: {
	siteId: string;
	activationId: string;
	planId: string;
}) {
	return `ari:cloud:jira:${siteId}:plan/activation/${activationId}/${planId}`;
}

type PlansHorizontalNavigationEntryPointComponent = EntryPointComponent<
	{
		horizontalNavPlansQuery: HorizontalNavPlansQuery;
	},
	{},
	HorizontalNavPlansEntrypointProps,
	{}
>;

export const plansHorizontalNavigationEntryPoint: EntryPoint<
	PlansHorizontalNavigationEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<PlansHorizontalNavigationEntryPointComponent>(
		/* webpackChunkName: "async-plans-horizontal-nav-entrypoint" */ './ui/HorizontalNavPlans.tsx',
		{
			includeInShellOnlySsrBundle: true,
			// can't be true yet as plans use window.location.pathname at module level & create redux reducers at module level - both not import lifting friendly
			entryFsSsrLiftImportToModule: false,
		},
	),
	getPreloadProps: ({
		context: { match },
		tenantContext: { cloudId, activationId, isAnonymous },
	}: EntryPointRouteParams) => {
		const planId = match.params.planId;
		const scenarioId = match.params.scenarioId;
		if (!planId) {
			throw new Error('Plan Id is missing');
		}
		if (!scenarioId) {
			throw new Error('Scenario Id is missing');
		}
		if (isAnonymous) {
			// Anonymous users cannot access plans — skip the network request entirely.
			// loadEntryPoint treats a return value without a `queries` key as a no-op
			// and never calls loadQuery, preventing the 403 from the GQL gateway.
			return {};
		}
		const planScenarioAri = toPlanScenarioAri({
			siteId: cloudId,
			activationId,
			planId,
			scenarioId,
		});
		const planAri = toPlanAri({
			siteId: cloudId,
			activationId,
			planId,
		});
		return {
			queries: {
				horizontalNavPlansQuery: {
					options: {
						fetchPolicy: 'store-and-network' as const,
					},
					parameters,
					variables: {
						first: NAVIGATION_ITEMS_PAGE_SIZE,
						cloudId,
						containerNavigationInput: {
							scopeId: planScenarioAri,
						},
						planId: planAri,
						scopeId: planScenarioAri,
					},
				},
			},
		};
	},
});
